import { css } from 'styled-components';
import { darken, lighten, transparentize } from 'polished';
import { blue, green, orange, red } from './variables';

export default css`
  --text: #d5d9dd;
  --body-bg: #232427;

  --bg: #2b2c30;
  --bg-1: #323338;
  --bg-2: #2a2b2f;
  --bg-3: #393b40;
  --bg-4: #707379;
  --bg-5: #dde1e4;
  --bg-6: #eaedf0;
  --bg-7: #f7f8f9;

  --skeleton-color: var(--bg-3);
  --skeleton-sheen-color: #4a4d53;

  --primary: ${red};
  --secondary: ${darken(0.15, red)};
  --red: ${red};
  --error: var(--btn-primary-bg);

  // Layout.
  --logo-color: var(--menu-logo-color);
  --box-shadow: 0 0 15px 0 rgb(0 0 0 / 30%);
  --box-shadow-component: 0 2px 6px 0 rgb(0 0 0 / 50%);
  --menu-bg: var(--bg-1);
  --menu-text: #a7aaaf;
  --menu-text-active: #ffffff;
  --menu-active-bg: var(--bg-2);
  --menu-category-color: #878b91;
  --menu-logo-color: #ffffff;
  --menu-border-color: var(--bg-3);
  --menu-active-icon-bg: #3a7ecb;

  // Type.
  --title-text: #ffffff;
  --text-muted: #98a2af;
  --text-strong: var(--title-text);

  // Navbar.
  --navbar-button-bg: var(--bg-3);
  --navbar-button-border-color: var(--bg-2);
  --navbar-button-text: var(--bg-6);
  --navbar-icon-color: var(--bg-6);
  --navbar-menu-bg: var(--navbar-button-bg);
  --navbar-menu-border-color: var(--bg-4);
  --navbar-menu-link: var(--bg-6);
  --navbar-menu-link-hover: var(--bg-7);
  --navbar-menu-link-hover-bg: var(--bg-2);
  --navbar-box-shadow: none;

  // Buttons
  --btn-box-shadow: none;

  // Links
  --link: #71b7f8;
  --link-hover: #a3d0ff;

  // Spinner
  --spinner-color: var(--link);

  // Modal
  --modal-icon: var(--text);
  --modal-overlay-bg: rgba(0, 0, 0, 0.85);
  --wa-color-overlay-modal: rgba(0, 0, 0, 0.6);
  --modal-header-bg: var(--bg-3);
  --modal-footer-bg: var(--bg-3);

  // Table
  --table-header-bg: var(--bg-3);
  --table-header-text: var(--title-text);
  --table-header-border: var(--bg-4);
  --table-header-seperator: var(--bg-4);
  --table-row-bg: var(--bg);
  --table-row-bg-alt: var(--bg);
  --table-row-border: var(--bg-3);

  // Select
  --select-height: 2.5rem;
  --select-placeholder: var(--form-field-placeholder);
  --select-text: var(--form-input-text);
  --select-bg: var(--form-input-bg);
  --select-option-bg: var(--bg-2);
  --select-option-hover-bg: var(--bg-3);
  --select-option-selected: var(--bg);
  --select-option-selected-bg: var(--bg-5);
  --select-option-disabled: var(--text);
  --select-option-disabled-bg: var(--bg-6);
  --select-multi-value-border: var(--bg-4);
  --select-multi-value-bg: var(--bg-3);
  --select-border-width: var(--form-input-border-width);
  --select-border-style: var(--form-input-border-style);
  --select-border-color: var(--form-input-border-color);
  --select-border-radius: var(--form-input-border-radius);
  --select-icon: var(--form-input-text);

  // Form
  --form-bg: var(--bg);
  --form-field-error-text: var(--error);
  --form-field-required: var(--error);
  --form-field-placeholder: var(--bg-4);
  --form-input-bg: var(--bg-1);
  --form-input-text: var(--text);
  --form-input-border-color: var(--bg-4);
  --form-textarea-preview-bg: var(--bg-1);
  --form-checkbox-unchecked: var(--bg-1);
  --form-checkbox-unchecked-border: var(--bg-4);
  --form-checkbox-checked: var(--bg-1);
  --form-checkbox-checked-border: var(--bg-4);
  --form-select-menu-border-width: 1px;
  --form-select-menu-border-style: solid;
  --form-select-menu-border-color: var(--bg-4);
  --form-box-shadow: none;
  --dns-record-form-bg: var(--body-bg);

  // Data-manager.
  --data-manager-select-bg: var(--bg-3);
  --data-manager-select-text: var(--text);
  --data-manager-select-box-shadow: var(--btn-box-shadow);
  --data-manager-select-border-width: var(--btn-border-width);
  --data-manager-select-border-radius: var(--btn-border-radius);
  --data-manager-select-border-color: var(--bg-4);
  --data-manager-select-menu-border-width: 1px;
  --data-manager-select-menu-border-style: solid;
  --data-manager-select-menu-border-color: var(--bg-4);
  --data-manager-select-option: var(--bg-5);
  --data-manager-select-option-bg: var(--bg-3);
  --data-manager-select-option-hover: var(--bg-7);
  --data-manager-select-option-hover-bg: var(--bg-2);
  --data-manager-select-option-selected: var(--text);
  --data-manager-select-option-selected-bg: var(--bg-4);
  --data-manager-select-multi-value-border: var(--form-select-multi-value-border);

  // Toggle buttons.
  --data-manager-toggle-button-bg: transparent;
  --data-manager-toggle-button-text: var(--text);

  // Footer.
  --data-manager-footer-text: var(--link);
  --data-manager-footer-bg: transparent;
  --data-manager-footer-active-text: var(--text-white);
  --data-manager-footer-active-bg: var(--link);

  // Label list.
  --label-list-header: var(--bg-3);
  --label-list-title: var(--title-text);

  // Badges.
  --role-badge-bg: var(--bg-3);
  --action-hover-bg: var(--bg-3);

  // Dropdown.
  --dropdown-bg-hover: var(--bg-3);
  --dropdown-bg-hover-alternate: var(--bg-2);
  --dropdown-border-color-hover: var(--bg-3);
  --dropdown-menu-bg: var(--navbar-menu-bg);
  --dropdown-menu-border-color: var(--navbar-menu-border-color);
  --dropdown-menu-item-hover-color: var(--navbar-menu-link-hover);
  --dropdown-menu-item-hover-bg: var(--navbar-menu-link-hover-bg);

  // Dropdown accents
  --dropdown-regular: var(--text);
  --dropdown-regular-hover: var(--dropdown-menu-item-hover-color);
  --dropdown-info: var(--link);
  --dropdown-info-hover: var(--link-hover);
  --dropdown-warning: ${orange};
  --dropdown-warning-hover: ${lighten(0.05, orange)};
  --dropdown-error: var(--error);
  --dropdown-error-hover: ${lighten(0.05, '#ff4a5b')};
  --dropdown-success: ${green};
  --dropdown-success-hover: ${lighten(0.05, green)};

  // Highlight
  --highlight-color: ${transparentize(0.45, blue)};

  // Boolean
  --boolean-false: var(--error);

  // Messages
  --message-bg: var(--bg);
  --message-header-bg: var(--bg-1);
  --message-header-button-bg: var(--bg-3);
  --message-border-left-outgoing: var(--green);
  --message-border-left-outgoing-hover: ${lighten(0.1, green)};
  --message-border-left-incoming: var(--link);

  // Alerts
  --alert-regular-bg: var(--bg-3);
  --alert-regular-border: var(--bg-4);

  --alert-info-border: ${blue};
  --alert-info-bg: ${transparentize(0.9, blue)};

  --alert-warning-border: ${orange};
  --alert-warning-bg: ${transparentize(0.9, orange)};

  // Search highlight.
  --mark-bg: ${transparentize(0.7, orange)};
  --mark-text: var(--bg-7);

  --alert-error-border: ${red};
  --alert-error-bg: ${transparentize(0.9, red)};

  --alert-success-border: ${green};
  --alert-success-bg: ${transparentize(0.9, green)};

  // Date picker
  --datepicker-day-selected-bg: var(--link);
  --datepicker-day-selected-bg-middle: var(--alert-info-bg);

  // Tooltip
  --rt-color-dark: var(--bg-3) !important;
  --rt-border-color: var(--bg-4) !important;

  // Tab group
  --tab-group-border: var(--bg-4);
  --tab-group-color: var(--title-text);
  --tab-group-active-color: var(--link);
  --tab-group-hover-color: var(--link-hover);

  // Dashboard
  --tile-button-bg: var(--bg-3);
  --tile-button-bg-hover: #3f4247;

  // Action list
  --action-list-row-bg: var(--bg);
  --action-list-row-bg-alt: var(--bg-3);
  --action-sub-action-border: var(--bg-4);

  // Toggle
  --toggle-bg: var(--bg-4);

  // Markdown
  --markdown-blockquote-bg: var(--bg-3);
  --markdown-pre-bg: var(--bg-3);
  --markdown-table-row-bg: var(--bg-3);
  --markdown-table-row-alt-bg: var(--bg-2);
  --markdown-h6-color: var(--text-white);

  // Search
  --search-row-hover-border: var(--bg-4);
`;
